/* eslint-disable prettier/prettier */
import React, { useMemo, useState } from 'react';
import { useEffect } from 'react';

import moment from 'moment';

import Button from 'components/Buttons/Button';
import Tags from 'components/Tags';
import ExpirationTag from 'components/Tags/ExpirationTag';
import PreBookTag from 'components/Tags/PreBookTag';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { RepprovalReason } from 'models/repprovalReason';
import { RequestedItem } from 'models/requestHistory';
import { TravelStatus } from 'models/travelStatus';
import AdminTravelApprovalDetails from 'screens/containers/AdminTravelApprovalDetails';
import { getFormattedPrice } from 'utils/currency';
import { expirationHours, splitDate, getFlightClass } from 'utils/generic';
import { statusTranslation, StatusTag } from 'utils/statusTag';
import { ReactComponent as TicketIcon } from 'assets/images/private-fare.svg';
import { getTurnDays } from '../DataCardGroupedFlights/utils';
import Details from './Details';
import DetailsContent from './DetailsContent';
import Tooltip from 'components/Tooltips/Tooltip';
import {
  Card,
  Header,
  Info,
  Section,
  DestinationDetail,
  Title,
  UserLabel,
  ButtonContainer,
  Price,
  Traveler,
  Destination,
  PlaneIcon,
  BedIcon,
  MoneyIcon,
  GroupIcon,
  GuestIcon,
  FlightDetails,
  AutoDetails,
  CarIcon,
  ArrowIcon,
  AirlineLogo,
  FlightLineBottomTextStrong,
  FlightTimeTurnDays,
  PrivateFareIconContainer,
  PrivateFareMessage,
  ContainerItemContainer,
  PrivateFareMessageText,
  TravelerItemContainer,
  OperatorName,
  PayAtDestination,
  CheckCircleIcon,
  PayAtDestinationText,
} from './styles';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';
import { ThemeType } from 'theme/theme';
import { useTheme } from 'styled-components';
interface Props {
  item: RequestedItem;
  approval?: boolean;
  submitting?: boolean;
  showDetails?: boolean;
  forceRefresh?: boolean;
  reasons?: RepprovalReason[];
  onConfirm?: (id: string) => void;
  onDecline?: (id: string, reason?: RepprovalReason) => void;
  onClick?: (type: 'decline' | 'approve') => void;
  onDetailsClose?: () => void;
  onRefreshed?: () => void;
}

const CardInfo = ({ item }: { item: RequestedItem }) => {
  const theme = useTheme() as ThemeType;
  const { t } = useStoreTranslation();
  const formatDate = (date: string) => {
    const splittedDate = splitDate(date);
    return moment(splittedDate).format('ddd[,] DD MMM');
  };
  const formatTime = (date: string) => moment(date).format('hh:mm A');

  const turnDaysDeparture = getTurnDays(item.travelDepartureStartDate ?? '', item.travelDepartureEndDate ?? '');
  const turnDaysReturn = getTurnDays(item.travelReturnStartDate ?? '', item.travelReturnEndDate ?? '');

  if (item.type === TravelType.Vehicle)
    return (
      <>
        <AutoDetails>
          <CarIcon />
          <div>
            <Destination>
              {t('requestCard.autoCity', { city: item.vehiclePickUpCityName || item.travelDeparture })}
              {item.travelDeparture !== item.travelDestiny && (
                <>
                  <ArrowIcon />
                  {item.vehicleDropoffCityName || item.travelDestiny}
                </>
              )}
            </Destination>
            <DestinationDetail>
              {formatDate(item.travelDate)} {formatTime(item.travelDate.replaceAll('Z', ''))} -{' '}
              {formatDate(item.returnDate)} {formatTime(item.returnDate.replaceAll('Z', ''))}
            </DestinationDetail>
            <DestinationDetail>
              {item.travelNumberOfDays === 1
                ? t('requestCard.autoDay')
                : t('requestCard.autoDays', {
                    days: item.travelNumberOfDays,
                  })}
            </DestinationDetail>
          </div>
        </AutoDetails>
      </>
    );

  if (item.type === TravelType.Hotel) {
    //hay que agregarlo en api
    const havePayAtDestination = item.payAtDestination;
    return (
      <>
        <BedIcon />
        <div>
          <Destination>{t('requestCard.hotelCity', { city: item.travelDestiny })}</Destination>
          <DestinationDetail>
            {t('requestCard.hotelDate', {
              dateIn: formatDate(item.travelDate),
              dateOut: formatDate(item.returnDate),
            })}
          </DestinationDetail>
          <DestinationDetail>
            {item.travelNumberOfDays === 1
              ? t('requestCard.hotelNight', {
                  days: item.travelNumberOfDays,
                })
              : t('requestCard.hotelNights', {
                  days: item.travelNumberOfDays,
                })}
          </DestinationDetail>
          {havePayAtDestination && (
            <PayAtDestination>
              <CheckCircleIcon />
              <PayAtDestinationText>{t('hotelsCard.payAtDestination')}</PayAtDestinationText>
            </PayAtDestination>
          )}
        </div>
      </>
    );
  }
  if (item.type === TravelType.Flight)
    return (
      <>
        {(item.travelAirStretch === 'Direct' || item.travelAirStretch === 'Round-Trip') && (
          <FlightDetails>
            <PlaneIcon />
            <div>
              <Destination>{`${item.travelDeparture} -> ${item.travelDestinyFirstSector}`}</Destination>
              <DestinationDetail>
                {t('requestCard.flightDate', {
                  date: formatDate(item.travelDate),
                })}
              </DestinationDetail>
              <DestinationDetail>
                {item.firstNumberOfStops === 0
                  ? t('requestCard.noStops')
                  : t('requestCard.flightStops', { count: item.firstNumberOfStops })}
              </DestinationDetail>

              {item.travelAirlineDeparture && item.travelAirlineCodeDeparture && (
                <DestinationDetail>
                  <AirlineLogo airline={item.travelAirlineCodeDeparture} />
                  &nbsp;{item.travelAirlineDeparture}
                </DestinationDetail>
              )}

              {item.opAirLineCode && item.opAirLineName && <OperatorName>{t('flightsCard.detailCard.operatorAirline', { airlineName: `${item.opAirLineName}` })}</OperatorName>}

              {/* Sector con turnDaysDeparture */}
              {item.travelDepartureStartDate && item.travelDepartureEndDate && turnDaysDeparture !== 0 && (
                <FlightTimeTurnDays>
                  {formatTime(item.travelDepartureStartDate.replaceAll('Z', ''))} -{' '}
                  {formatTime(item.travelDepartureEndDate.replaceAll('Z', ''))}
                  {<>{` (${turnDaysDeparture > 0 ? '+' : '-'} ${turnDaysDeparture})`}</>}
                </FlightTimeTurnDays>
              )}

              {/* Sector sin turnDaysDeparture */}
              {item.travelDepartureStartDate && item.travelDepartureEndDate && turnDaysDeparture === 0 && (
                <DestinationDetail>
                  {formatTime(item.travelDepartureStartDate.replaceAll('Z', ''))} -{' '}
                  {formatTime(item.travelDepartureEndDate.replaceAll('Z', ''))}
                </DestinationDetail>
              )}

              {item.cabin && (
                <FlightDetails>
                  <FlightLineBottomTextStrong>
                    <strong>{t(getFlightClass(item.cabin))}</strong>
                  </FlightLineBottomTextStrong>
                </FlightDetails>
              )}
            </div>
          </FlightDetails>
        )}
        {item.travelAirStretch === 'Round-Trip' && (
          <FlightDetails $right>
            <PlaneIcon $return />
            <div>
              <Destination>{`${item.travelDepartureLastSector} -> ${item.travelDestiny}`}</Destination>
              <DestinationDetail>
                {t('requestCard.flightDate', {
                  date: formatDate(item.returnDate),
                })}
              </DestinationDetail>
              <DestinationDetail>
                {item.lastNumberOfStops === 0
                  ? t('requestCard.noStops')
                  : t('requestCard.flightStops', { count: item.lastNumberOfStops })}
              </DestinationDetail>
              {item.travelAirlineReturn && (
                <DestinationDetail>
                  <AirlineLogo airline={item.travelAirlineCodeReturn} />
                  &nbsp;{item.travelAirlineReturn}
                </DestinationDetail>
              )}

              {item.opAirLineCodeReturn && item.opAirLineNameReturn && <OperatorName>{t('flightsCard.detailCard.operatorAirline', { airlineName: `${item.opAirLineNameReturn}` })}</OperatorName>}

              {/* Sector con turnDaysReturn */}
              {item.travelReturnStartDate && item.travelReturnEndDate && turnDaysReturn !== 0 && (
                <FlightTimeTurnDays>
                  {formatTime(item.travelReturnStartDate.replaceAll('Z', ''))} -{' '}
                  {formatTime(item.travelReturnEndDate.replaceAll('Z', ''))}
                  {<>{` (${turnDaysReturn > 0 ? '+' : '-'} ${turnDaysReturn})`}</>}
                </FlightTimeTurnDays>
              )}

              {/* Sector sin turnDaysReturn */}
              {item.travelReturnStartDate && item.travelReturnEndDate && turnDaysReturn === 0 && (
                <DestinationDetail>
                  {formatTime(item.travelReturnStartDate.replaceAll('Z', ''))} -{' '}
                  {formatTime(item.travelReturnEndDate.replaceAll('Z', ''))}
                </DestinationDetail>
              )}

              {item.cabin2 && (
                <FlightDetails>
                  <FlightLineBottomTextStrong>
                    <strong>{t(getFlightClass(item.cabin2))}</strong>
                  </FlightLineBottomTextStrong>
                </FlightDetails>
              )}
            </div>
          </FlightDetails>
        )}
        {item.travelAirStretch === 'Multi-Destination' && (
          <FlightDetails>
            <PlaneIcon />
            <div>
              <Destination>{t('requestCard.multiDestination')}</Destination>
              <DestinationDetail>
                {t('requestCard.flightDate', {
                  date: formatDate(item.travelDate),
                })}
                &nbsp; - &nbsp;
                {t('requestCard.flightDate', {
                  date: formatDate(item.returnDate),
                })}
              </DestinationDetail>
              <DestinationDetail>
                {`${item.travelDeparture} -> ${item.travelDestinyFirstSector}  ...  ${item.travelDepartureLastSector} -> ${item.travelDestiny}`}
              </DestinationDetail>
              {item.travelAirlineDeparture && item.travelAirlineReturn && (
                <DestinationDetail>
                  <AirlineLogo airline={item.travelAirlineCodeDeparture} />
                  &nbsp;{item.travelAirlineDeparture} ... <AirlineLogo airline={item.travelAirlineCodeReturn} />
                  &nbsp;{item.travelAirlineReturn}
                </DestinationDetail>
              )}
              
              {item.opAirLineCode && item.opAirLineName && <OperatorName>{t('flightsCard.detailCard.operatorAirline', { airlineName: `${item.opAirLineName}` })}</OperatorName>}

              {item.travelDepartureStartDate &&
                item.travelDepartureEndDate &&
                item.travelReturnStartDate &&
                item.travelReturnEndDate && (
                  <DestinationDetail>
                    {formatTime(item.travelDepartureStartDate.replaceAll('Z', ''))} -{' '}
                    {formatTime(item.travelDepartureEndDate.replaceAll('Z', ''))} ...{' '}
                    {formatTime(item.travelReturnStartDate.replaceAll('Z', ''))} -{' '}
                    {formatTime(item.travelReturnEndDate.replaceAll('Z', ''))}
                  </DestinationDetail>
                )}

              {item.cabin && (
                <FlightDetails>
                  <FlightLineBottomTextStrong>
                    <strong>{t(getFlightClass(item.cabin))}</strong>
                  </FlightLineBottomTextStrong>
                </FlightDetails>
              )}
            </div>
          </FlightDetails>
        )}
      </>
    );

  if (item.type === TravelType.ExtraCharge)
    return (
      <>
        <FlightDetails>
          <MoneyIcon />
          <div>
            <Destination>{t('requestCard.extraCharge')}</Destination>
            <DestinationDetail>{item.reason}</DestinationDetail>
            <DestinationDetail>&nbsp;</DestinationDetail>
          </div>
        </FlightDetails>
      </>
    );

  return null;
};

const RequestCard = ({
  item,
  approval,
  onClick,
  onConfirm,
  onDecline,
  submitting,
  showDetails = false,
  onDetailsClose,
  forceRefresh,
  onRefreshed,
  reasons,
}: Props) => {
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined, currency: string) => getFormattedPrice({ value, currency, t });
  const formattedPriceTotal = formattedPrice(item.travelTotalValue || 0, item.currency);
  const alternativeFormattedPriceTotal = formattedPrice(item.alternativeTravelTotalValue || 0, item.alternativeCurrency);
  const theme = useTheme() as ThemeType;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showAllDetails, setShowAllDetails] = useState(showDetails ?? false);

  useEffect(() => {
    if (showDetails) {
      setShowAllDetails(true);
    }
  }, [showDetails]);

  const status: StatusTag = useMemo(() => {
    if (!item)
      return {
        text: '',
        icon: 'clock',
        color: 'gray',
      };
    const text = statusTranslation(item?.travelStatus, t);
    switch (item?.travelStatus) {
      case TravelStatus.Approved:
        return {
          text,
          icon: 'check',
          color: 'green',
        };
      case TravelStatus.Confirmed:
        return {
          text,
          icon: 'check',
          color: 'green',
        };
      case TravelStatus.Paid:
        return {
          text,
          icon: 'check',
          color: 'green',
        };
      case TravelStatus.Expired:
        return {
          text,
          icon: 'x',
          color: 'red',
        };
      case TravelStatus.Reproved:
        return {
          text,
          icon: 'x',
          color: 'red',
        };
      case TravelStatus.ErrorAtSending:
        return {
          text,
          icon: 'x',
          color: 'red',
        };
      case TravelStatus.PaymentFailure:
        return {
          text,
          icon: 'x',
          color: 'red',
        };
      default:
        return {
          text,
          icon: 'clock',
          color: 'gray',
        };
    }
  }, [item, t]);

  const handleApprovalOrDecline = (travelType: 'decline' | 'approve') => {
    if (onClick) onClick(travelType);
  };

  const handleOpenAllDetails = () => setShowAllDetails(true);
  const handleCloseAllDetails = () => {
    setShowAllDetails(false);
    if (onDetailsClose) onDetailsClose();
  };

  const headerSubtitleTooltip = () => {
    if (item && item.travelers && item.travelers.length > 1) {
      return (
    <Tooltip
          whiteColor={true}
          event="hover"
          content={
            getTooltipContent()
          }
        >
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>          
            {item.userName} {item.travelers && item.travelers.length > 1 ? '+ ' + (item.travelers.length - 1) : ''}
           &nbsp; 
          <img width={20} height={20} src="/corporate/images/advice-2.svg" alt="" />
        </div>
      </Tooltip>
      );
    } else {
      return (
        <span style={{ display: 'inline'}}>
          {item.userName}
        </span>
      );
    }    
  };

  const getTooltipContent = () => (
    <>
      <div style={{ marginBottom: 18, fontWeight: 600 }}>
        {t('flightsSearch.additionalTraveler')}
      </div>
      {item.travelers.slice(1).map((t, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <img src="/corporate/images/check-copy.svg" alt="" width={14} height={14} />
          <span>
            {t.firstName} {t.lastName}
          </span>
        </div>
      ))}
    </>
  );

  const companyConfiguration = useCompanyConfiguration();

  return (
    <Card>
      <Header>
        <Info>
          <Title>
            {t('requestCard.number', { companyName: companyConfiguration?.companyName, number: item.itineraryId })}
          </Title>
        </Info>
        {item.expirationDate ? (
          item.preBook ? (
            <PreBookTag time={expirationHours(item.expirationDate)} />
          ) : (
            <ExpirationTag showIcon time={expirationHours(item.expirationDate)} />
          )
        ) : (
          <Tags color={status.color} icon={status.icon}>
            {status.text}
          </Tags>
        )}
      </Header>

      <Section>
        <TravelerItemContainer>
          <Traveler>
            {headerSubtitleTooltip()}            
          </Traveler>
          <UserLabel>
            {item.isGuest ? (
              <>
                <GuestIcon color="currentColor" height={16} />
                {t('requestCard.invited')}
              </>
            ) : (
              <>
                <GroupIcon color="currentColor" height={16} />
                {item.userGroup}
              </>
            )}
          </UserLabel>
        </TravelerItemContainer>
        <ContainerItemContainer>
          <Price>
            {item.currency && (
              <>
                {formattedPriceTotal?.formattedWithoutCode}{' '}
                {formattedPriceTotal?.showCode ? <small>{formattedPriceTotal?.currency}</small> : ''}
              </>
            )}            
          </Price>
          <Price>
            {item.alternativeCurrency && (
              <>
                &nbsp;{t('alternativePrice.or')}&nbsp; 
                {alternativeFormattedPriceTotal?.formattedWithoutCode}{' '}
                {alternativeFormattedPriceTotal?.showCode ? <small>{alternativeFormattedPriceTotal?.currency}</small> : ''}
              </>
            )}            
          </Price>
          {item.hasPrivateFare && (
            <PrivateFareMessage>
              <PrivateFareIconContainer>
                <TicketIcon stroke={theme.primaryBackgroundColor} />                
              </PrivateFareIconContainer>
              <PrivateFareMessageText>{t('flightDetails.privateFare')}</PrivateFareMessageText>
              
            </PrivateFareMessage>
          )}
        </ContainerItemContainer>
      </Section>

      <Section>
        <CardInfo item={item} />
      </Section>

      <Details
        open={detailsOpen}
        onToggle={setDetailsOpen}
        outOfPolicy={item.breakRulesMessage && item.breakRulesMessage.length > 0}
      >
        <DetailsContent item={item} onClickShowAllDetails={handleOpenAllDetails} />

        {approval && onConfirm && onDecline ? (
          <AdminTravelApprovalDetails
            id={item.travelId || ''}
            show={showAllDetails}
            onClose={handleCloseAllDetails}
            onConfirm={() => onConfirm(item.travelId || '')}
            onDecline={(reason) => onDecline(item.travelId || '', reason)}
            submitting={submitting}
            forceRefresh={forceRefresh}
            onRefreshed={onRefreshed}
            reasons={reasons}
            lowestLogicalFareList={item?.lowestLogicalFareList}
          />
        ) : (
          <AdminTravelApprovalDetails
            id={item.travelId || ''}
            show={showAllDetails}
            onClose={handleCloseAllDetails}
            forceRefresh={forceRefresh}
            onRefreshed={onRefreshed}
            lowestLogicalFareList={item?.lowestLogicalFareList}
          />
        )}
      </Details>

      {approval && (
        <ButtonContainer>
          <Button
            disabled={item.superAdminViewParentProfile}
            invertedColor={!item.superAdminViewParentProfile}
            onClick={() => handleApprovalOrDecline('decline')}
          >
            {t('requestCard.declineButton')} 
          </Button>
          <Button disabled={item.superAdminViewParentProfile} onClick={() => handleApprovalOrDecline('approve')}>
            {item.paymentFailure === true ? t('requestCard.retryApproval') : t('requestCard.acceptButton')}
          </Button>
        </ButtonContainer>
      )}
    </Card>
  );
};

export default RequestCard;
